import React, { useEffect, useState, useRef } from 'react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Plus, Trash2, Save, FileText, ImageIcon, Upload, MessageCircle, Send, Eye, ShieldAlert, Tags, Ruler, X, Download, Loader2 } from 'lucide-react';
import { api, API } from '../../lib/store';
import { toast } from 'sonner';
import MarkdownInput from '../ui/MarkdownInput';
import AuditHealQuotationsDialog from './AuditHealQuotationsDialog';
import MislabeledProductsDialog from './MislabeledProductsDialog';
import { logger } from '../../lib/logger';

/**
 * Admin → Settings tab. Lets the admin edit:
 *   • Company name + address lines
 *   • Right-side header text (mobile / GST line)
 *   • Terms & conditions (a list of strings)
 *   • Bank details (free-form multi-line text)
 *
 * Saves to /api/settings (PUT, admin-only). The PDF generator picks these
 * up automatically on the next download.
 */
export default function SettingsManagement() {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [backingUp, setBackingUp] = useState(false);
  const fileInputRef = useRef(null);
  // Bumped each time the admin uploads/removes the logo so the <img>
  // cache-busts and the freshest image is shown.
  const [logoVersion, setLogoVersion] = useState(0);
  const [uploading, setUploading] = useState(false);
  // Daily WhatsApp digest test/preview state
  const [sendingTest, setSendingTest] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [previewText, setPreviewText] = useState('');
  // Diagnostic health snapshot — populated from /admin/daily-summary/health.
  const [digestHealth, setDigestHealth] = useState(null);
  const [loadingHealth, setLoadingHealth] = useState(false);
  const [sendingTextTest, setSendingTextTest] = useState(false);
  const [auditOpen, setAuditOpen] = useState(false);
  const [mislabelOpen, setMislabelOpen] = useState(false);
  // DoubleTick API key verification — surfaces "key rejected" vs
  // "template issue" vs "network error" so admins get an actionable
  // message instead of a raw 403 blob.
  const [verifying, setVerifying] = useState(false);
  const [verifyResult, setVerifyResult] = useState(null);
  // Iter-123 permanent fix — admins can rotate the DoubleTick API key
  // from this UI (persisted to the database, hot-reloaded on the very next
  // WhatsApp call — no redeploy or restart needed).
  const [dtCreds, setDtCreds] = useState(null); // {key_last8, waba_number, source, updated_at}
  const [newApiKey, setNewApiKey] = useState('');
  const [newWaba, setNewWaba] = useState('');
  const [savingCreds, setSavingCreds] = useState(false);
  // Iter-124 — sample walk-in / lead reminder test button state
  const [testingWalkin, setTestingWalkin] = useState(false);
  const [testingLead, setTestingLead] = useState(false);
  const [savingTerms, setSavingTerms] = useState(false);

  useEffect(() => {
    api.getSettings().then(res => setData(res.data)).catch(err => {
      logger.error(err);
      toast.error('Failed to load settings');
    });
  }, []);

  // Load the digest health snapshot once when the settings page mounts so
  // the admin can immediately see whether the 9 PM job is healthy.
  const loadDigestHealth = React.useCallback(async () => {
    setLoadingHealth(true);
    try {
      const res = await api.getDailySummaryHealth();
      setDigestHealth(res.data);
    } catch (err) {
      logger.warn('Failed to load digest health:', err);
    } finally {
      setLoadingHealth(false);
    }
  }, []);
  useEffect(() => { loadDigestHealth(); }, [loadDigestHealth]);

  // Load current DoubleTick creds (masked) so admin sees the last-8 chars
  // + source (db|env) + when they were last rotated.
  const loadDtCreds = React.useCallback(async () => {
    try {
      const res = await api.getDoubletickCredentials();
      setDtCreds(res.data);
    } catch (err) {
      logger.warn('Could not load DoubleTick credentials:', err);
    }
  }, []);

  useEffect(() => { loadDtCreds(); }, [loadDtCreds]);

  if (!data) {
    return <div className="flex items-center justify-center gap-2 py-10 text-sm text-heda-mute"><Loader2 className="w-5 h-5 animate-spin text-heda-gold" /> Loading settings…</div>;
  }

  const updateTerm = (idx, val) => {
    const next = [...(data.terms || [])];
    next[idx] = val;
    setData({ ...data, terms: next });
  };
  const addTerm = () => setData({ ...data, terms: [...(data.terms || []), ''] });
  const removeTerm = (idx) => {
    const next = (data.terms || []).filter((_, i) => i !== idx);
    setData({ ...data, terms: next });
  };
  // Section-local save for T&C so admins don't have to scroll
  // to the bottom-of-page "Save Settings" to persist Add/Edit/Delete —
  // reason many users thought terms weren't saving. Mirrors the
  // DoubleTick creds "Save & apply immediately" pattern (line ~741).
  // Same underlying PUT /api/settings the page-level save uses, but
  // sends *only* the terms array so no other unsaved edits are
  // accidentally committed.
  const saveTerms = async () => {
    setSavingTerms(true);
    try {
      const cleaned = (data.terms || []).map(t => (t || '').trim()).filter(Boolean);
      const res = await api.updateSettings({ terms: cleaned });
      // Backend returns the full settings doc — merge only the terms
      // field so other unsaved page-level edits (logo, bank, etc.)
      // aren't overwritten.
      setData(d => ({ ...d, terms: res.data.terms || [] }));
      toast.success('Terms saved — new PDFs will use these');
    } catch (err) {
      logger.error(err);
      toast.error(err.response?.data?.detail || 'Failed to save terms');
    } finally {
      setSavingTerms(false);
    }
  };

  const onLogoFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please pick an image file (PNG / JPG / WEBP)');
      return;
    }
    setUploading(true);
    try {
      await api.uploadLogo(file);
      setLogoVersion(v => v + 1);
      // Refresh settings flag so the "Remove" button appears.
      const fresh = await api.getSettings();
      setData(fresh.data);
      toast.success('Logo updated — new PDFs will use it');
    } catch (err) {
      logger.error(err);
      toast.error(err.response?.data?.detail || 'Failed to upload logo');
    } finally {
      setUploading(false);
    }
  };

  const onLogoRemove = async () => {
    if (!window.confirm('Remove the company logo? PDFs will fall back to the default HEDA logo until you upload a new one.')) return;
    try {
      await api.deleteLogo();
      setLogoVersion(v => v + 1);
      const fresh = await api.getSettings();
      setData(fresh.data);
      toast.success('Logo removed');
    } catch (err) {
      toast.error('Failed to remove logo');
    }
  };

  // Send a test WhatsApp digest to the configured/typed phone(s) immediately
  // — verifies the recipients are correct without waiting for the 9 PM cron.
  // The backend accepts multi-number input (comma / newline separated).
  // Download a full JSON backup of every database collection as a zip.
  // Admin-only; strictly read-only. Uses the same blob-download pattern
  // as the template / PDF endpoints elsewhere in the app.
  const handleDownloadBackup = async () => {
    setBackingUp(true);
    try {
      const res = await api.downloadBackup();
      const blob = new Blob([res.data], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);
      const today = new Date().toISOString().split('T')[0];
      const a = document.createElement('a');
      a.href = url;
      a.download = `heda_backup_${today}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Backup downloaded — store it somewhere safe.');
    } catch (err) {
      logger.error(err);
      toast.error(err.response?.data?.detail || 'Failed to generate backup. Please try again.');
    } finally {
      setBackingUp(false);
    }
  };

  // Send a test WhatsApp digest to the configured/typed phone(s) immediately
  // — verifies the recipients are correct without waiting for the 9 PM cron.
  // The backend accepts multi-number input (comma / newline separated).
  const sendTestDigest = async () => {
    const phone = (data.daily_summary_phone || '').trim();
    if (!phone) {
      toast.error('Enter at least one phone number first (and click Save Settings).');
      return;
    }
    setSendingTest(true);
    try {
      const res = await api.sendDailySummaryNow(phone);
      const recipients = res.data?.recipients || [];
      if (res.data?.sent) {
        const sentCount = recipients.filter(r => r.sent).length;
        const failedCount = recipients.length - sentCount;
        if (recipients.length > 1) {
          toast.success(
            failedCount === 0
              ? `Test digest sent to all ${sentCount} recipients`
              : `Sent to ${sentCount} of ${recipients.length} — ${failedCount} failed`
          );
        } else {
          toast.success(`Test digest sent to ${res.data.to}`);
        }
      } else {
        toast.error(`Could not send: ${res.data?.reason || res.data?.error || 'unknown error'}`);
      }
    } catch (err) {
      logger.error(err);
      toast.error(err.response?.data?.detail || 'Failed to send test digest');
    } finally {
      setSendingTest(false);
      loadDigestHealth();
    }
  };

  // Diagnostic: try the free-form TEXT endpoint to tell apart "API key
  // rejected by DoubleTick" (BOTH /text and /template fail with 403) vs
  // "template paused / not approved" (only /template fails).
  const sendTestText = async () => {
    const phone = (data.daily_summary_phone || '').trim();
    if (!phone) {
      toast.error('Enter a phone number first (and click Save Settings).');
      return;
    }
    setSendingTextTest(true);
    try {
      const first = phone.split(/[\n,;]+/).map(s => s.trim()).filter(Boolean)[0];
      const res = await api.sendDailySummaryTestText(first);
      if (res.data?.sent) {
        toast.success(`Free-form text sent to ${res.data.to} — your API key + WABA are healthy. The 403 is template-side.`);
      } else {
        toast.error(`Text send failed: ${res.data?.error || 'unknown'}`, { duration: 10000 });
      }
    } catch (err) {
      logger.error(err);
      toast.error(err.response?.data?.detail || 'Failed to test text send');
    } finally {
      setSendingTextTest(false);
      loadDigestHealth();
    }
  };

  // Probe DoubleTick with a lightweight healthcheck + templates list and
  // classify the failure — separates "API key rejected by DoubleTick"
  // (recurring 403) from network / template-approval issues so the
  // admin knows exactly which button to press next.
  const verifyDoubleTick = async () => {
    setVerifying(true);
    try {
      const res = await api.verifyDoubleTick();
      setVerifyResult(res.data);
      if (res.data?.ok) {
        toast.success('DoubleTick API key is valid.');
      } else {
        toast.error(res.data?.message || 'DoubleTick API key verification failed', { duration: 12000 });
      }
    } catch (err) {
      logger.error(err);
      setVerifyResult({ ok: false, verdict: 'error', message: 'Verification request failed', action: 'Retry.', probes: [] });
      toast.error('Verification request failed');
    } finally {
      setVerifying(false);
    }
  };

  // Iter-124 — sample walk-in / lead reminder senders. Lets admin
  // confirm end-to-end WhatsApp delivery without waiting for the next
  // real scheduled reminder to fire.
  const sendTestWalkinReminder = async () => {
    // Let the backend resolve the recipient from saved app_settings —
    // avoids the false "phone missing" toast when the admin hasn't just
    // edited the textarea. Backend's _resolve_recipient_phones("") reads
    // from the persisted store.
    setTestingWalkin(true);
    try {
      const res = await api.testWalkinReminder('');
      if (res.data?.sent) toast.success(`Sample walk-in reminder sent to ${res.data.to}. Check your WhatsApp.`);
      else toast.error(`Failed: ${res.data?.error || 'unknown'}`, { duration: 10000 });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Test send failed');
    } finally { setTestingWalkin(false); }
  };
  const sendTestLeadReminder = async () => {
    setTestingLead(true);
    try {
      const res = await api.testLeadReminder('');
      if (res.data?.sent) toast.success(`Sample lead follow-up sent to ${res.data.to}. Check your WhatsApp.`);
      else toast.error(`Failed: ${res.data?.error || 'unknown'}`, { duration: 10000 });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Test send failed');
    } finally { setTestingLead(false); }
  };

  const saveDtCreds = async () => {
    if (!newApiKey.trim() && !newWaba.trim()) {
      toast.info('Nothing to save — paste a new API key or WABA number first.');
      return;
    }
    setSavingCreds(true);
    try {
      const body = {};
      if (newApiKey.trim()) body.api_key = newApiKey.trim();
      if (newWaba.trim()) body.waba_number = newWaba.trim();
      await api.updateDoubletickCredentials(body);
      toast.success('Saved. The next WhatsApp send will use the updated credentials — no restart needed.');
      setNewApiKey('');
      setNewWaba('');
      setVerifyResult(null);
      await loadDtCreds();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save credentials');
    } finally {
      setSavingCreds(false);
    }
  };

  // Preview the digest message body without sending — quick sanity check.
  const previewDigest = async () => {
    setPreviewing(true);
    try {
      const res = await api.previewDailySummary();
      setPreviewText(res.data?.message || '(empty)');
    } catch (err) {
      logger.error(err);
      toast.error('Failed to load preview');
    } finally {
      setPreviewing(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        company_name: data.company_name || '',
        address_line1: data.address_line1 || '',
        address_line2: data.address_line2 || '',
        header_right_text: data.header_right_text || '',
        terms: (data.terms || []).map(t => (t || '').trim()).filter(Boolean),
        bank_details: data.bank_details || '',
        daily_summary_phone: (data.daily_summary_phone || '').trim(),
        // User-added custom size presets. Sanitised the same
        // way the frontend normalises them (lowercased, whitespace
        // stripped, dedup'd). Empty strings and non-"LxW" entries are
        // dropped so the backend can trust the shape.
        custom_size_presets: Array.from(new Set(
          (data.custom_size_presets || [])
            .map(s => (s || '').trim().toLowerCase().replace(/\s+/g, ''))
            .filter(s => s && s.includes('x'))
        )),
      };
      const res = await api.updateSettings(payload);
      setData(res.data);
      toast.success('Settings saved — new PDFs will use these values');
    } catch (err) {
      logger.error(err);
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4 max-w-3xl" data-testid="settings-management">
      {/* Company logo — appears in the PDF header. Falls back to the default
          HEDA logo if none is uploaded. */}
      <Card className="rounded-2xl border-heda-line bg-white shadow-none">
        <CardHeader className="border-b border-heda-line px-5 py-3">
          <CardTitle className="flex items-center gap-2 font-display text-[15px] font-bold text-heda-ink">
            <ImageIcon className="w-4 h-4 text-heda-gold" /> Company Logo
          </CardTitle>
        </CardHeader>
        <CardContent className="px-5 py-4 space-y-3">
          <div className="flex items-center gap-4">
            <div className="w-28 h-20 rounded-xl border border-heda-line bg-heda-cream overflow-hidden flex items-center justify-center">
              {data.has_logo ? (
                <img
                  key={logoVersion}
                  src={`${API}/settings/logo?v=${logoVersion}`}
                  alt="Current logo"
                  className="max-w-full max-h-full object-contain"
                  data-testid="settings-logo-preview"
                />
              ) : (
                <span className="text-[11px] text-heda-mute text-center px-2">No custom logo<br/>(using default)</span>
              )}
            </div>
            <div className="flex-1 space-y-2">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg,image/webp"
                onChange={(e) => onLogoFile(e.target.files?.[0])}
                className="hidden"
                data-testid="settings-logo-file"
              />
              <div className="flex gap-2 flex-wrap">
                <Button className="rounded-full border-heda-line bg-white text-heda-ink shadow-none hover:bg-white hover:border-heda-gold hover:text-heda-gold"
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  data-testid="settings-logo-upload-btn"
                >
                  <Upload className="w-3.5 h-3.5 mr-1.5" />
                  {uploading ? 'Uploading…' : data.has_logo ? 'Replace Logo' : 'Upload Logo'}
                </Button>
                {data.has_logo && (
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={onLogoRemove}
                    className="rounded-full hover:bg-heda-cream text-red-700 hover:bg-red-50"
                    data-testid="settings-logo-remove-btn"
                  >
                    <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Remove
                  </Button>
                )}
              </div>
              <p className="text-[11px] text-heda-mute">PNG / JPG / WEBP, up to 2 MB. Auto-fits to ~45 mm wide in the PDF.</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Header / Company info */}
      <Card className="rounded-2xl border-heda-line bg-white shadow-none">
        <CardHeader className="border-b border-heda-line px-5 py-3">
          <CardTitle className="flex items-center gap-2 font-display text-[15px] font-bold text-heda-ink">
            <FileText className="w-4 h-4 text-heda-gold" /> PDF Header
          </CardTitle>
        </CardHeader>
        <CardContent className="px-5 py-4 space-y-3">
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-heda-mute">Company Name</Label>
            <Input
              value={data.company_name || ''}
              onChange={e => setData({ ...data, company_name: e.target.value })}
              className="rounded-xl mt-1 bg-heda-cream/50"
              data-testid="settings-company-name"
            />
          </div>
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-heda-mute">Address Line 1</Label>
            <Input
              value={data.address_line1 || ''}
              onChange={e => setData({ ...data, address_line1: e.target.value })}
              className="rounded-xl mt-1 bg-heda-cream/50"
              data-testid="settings-address-1"
            />
          </div>
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-heda-mute">Address Line 2</Label>
            <Input
              value={data.address_line2 || ''}
              onChange={e => setData({ ...data, address_line2: e.target.value })}
              className="rounded-xl mt-1 bg-heda-cream/50"
              data-testid="settings-address-2"
            />
          </div>
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-heda-mute">Right-side Header Text (Mobile / GST)</Label>
            <Input
              value={data.header_right_text || ''}
              onChange={e => setData({ ...data, header_right_text: e.target.value })}
              className="rounded-xl mt-1 tabular-nums text-base md:text-sm bg-heda-cream/50"
              data-testid="settings-header-right"
              placeholder="MB: 8904002711 | GST: 29AAAFH8780L1ZR"
            />
            <p className="text-[11px] text-heda-mute mt-1">Shown in bold below the address on every PDF.</p>
          </div>
        </CardContent>
      </Card>

      {/* Terms */}
      <Card className="rounded-2xl border-heda-line bg-white shadow-none">
        <CardHeader className="flex flex-row items-center justify-between gap-3 space-y-0 border-b border-heda-line px-5 py-3">
          <CardTitle className="flex items-center gap-2 font-display text-[15px] font-bold text-heda-ink">Terms &amp; Conditions</CardTitle>
          <Button className="rounded-full border-heda-line bg-white text-heda-ink shadow-none hover:bg-white hover:border-heda-gold hover:text-heda-gold"
            size="sm"
            variant="outline"
            onClick={addTerm}
            data-testid="settings-add-term"
          >
            <Plus className="w-3.5 h-3.5 mr-1" /> Add Term
          </Button>
        </CardHeader>
        <CardContent className="px-5 py-4 space-y-2">
          {(data.terms || []).length === 0 && (
            <p className="text-xs text-heda-mute">No terms yet — click &quot;Add Term&quot; to add one.</p>
          )}
          {(data.terms || []).map((t, i) => (
            <div key={i} className="flex items-center gap-2">
              <span className="text-xs text-heda-sub w-6 shrink-0 font-mono">{i + 1}]</span>
              <MarkdownInput
                value={t}
                onChange={(val) => updateTerm(i, val)}
                className="flex-1"
                testId={`settings-term-${i}`}
              />
              <button
                onClick={() => removeTerm(i)}
                className="text-red-700 hover:bg-red-50 rounded-full p-2.5"
                aria-label="Remove term"
                data-testid={`settings-term-remove-${i}`}
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
          <p className="text-[11px] text-heda-mute mt-2">
            Use <code className="bg-heda-cream px-1 rounded-lg">**bold**</code> for <b>bold</b> and{' '}
            <code className="bg-heda-cream px-1 rounded-lg">*italic*</code> for <i>italic</i>, or click the B / I buttons.
          </p>
          {/* Section-local save so admins can persist Add /
              Edit / Delete without scrolling to page-bottom Save. */}
          <div className="flex justify-end pt-1">
            <Button
              size="sm"
              onClick={saveTerms}
              disabled={savingTerms}
              className="rounded-xl bg-heda-ink text-white shadow-none hover:bg-black h-9 text-xs"
              data-testid="settings-save-terms"
            >
              <Save className="w-3.5 h-3.5 mr-1" />
              {savingTerms ? 'Saving…' : 'Save Terms'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Bank details */}
      <Card className="rounded-2xl border-heda-line bg-white shadow-none">
        <CardHeader className="border-b border-heda-line px-5 py-3">
          <CardTitle className="flex items-center gap-2 font-display text-[15px] font-bold text-heda-ink">Bank Details</CardTitle>
        </CardHeader>
        <CardContent className="px-5 py-4">
          <Textarea
            value={data.bank_details || ''}
            onChange={e => setData({ ...data, bank_details: e.target.value })}
            rows={6}
            className="rounded-xl tabular-nums text-base md:text-sm bg-heda-cream/50"
            data-testid="settings-bank-details"
            placeholder="NAME OF BENEFICIARY: ...\nBANK NAME & BRANCH: ...\n..."
          />
          <p className="text-[11px] text-heda-mute mt-1">
            Use line breaks — each line appears on its own row in the PDF. Markdown: <code className="bg-heda-cream px-1 rounded-lg">**bold**</code> / <code className="bg-heda-cream px-1 rounded-lg">*italic*</code>.
          </p>
        </CardContent>
      </Card>

      {/* Daily WhatsApp Digest — owner gets a summary every day at 9 PM IST */}
      <Card className="rounded-2xl border-heda-line bg-white shadow-none">
        <CardHeader className="border-b border-heda-line px-5 py-3">
          <CardTitle className="flex items-center gap-2 font-display text-[15px] font-bold text-heda-ink">
            <MessageCircle className="w-4 h-4 text-heda-gold" /> Daily WhatsApp Digest
          </CardTitle>
        </CardHeader>
        <CardContent className="px-5 py-4 space-y-3">
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-heda-mute">Recipient Phone Number(s)</Label>
            <Textarea
              value={data.daily_summary_phone || ''}
              onChange={e => setData({ ...data, daily_summary_phone: e.target.value })}
              rows={3}
              className="rounded-xl mt-1 tabular-nums text-base md:text-sm bg-heda-cream/50"
              placeholder={"918904002711\n919663057711"}
              data-testid="settings-daily-summary-phone"
            />
            <p className="text-[11px] text-heda-mute mt-1">
              Owner&apos;s daily summary is sent here every evening at <strong>9:00 PM IST</strong> via WhatsApp.
              Add <strong>one number per line</strong> (or comma-separated). Use digits only with country code
              (e.g. <code className="bg-heda-cream px-1 rounded-lg">918904002711</code>) — every listed number will
              receive the message. Click <em>Save Settings</em> below after editing.
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button className="rounded-full border-heda-line bg-white text-heda-ink shadow-none hover:bg-white hover:border-heda-gold hover:text-heda-gold"
              type="button"
              size="sm"
              variant="outline"
              onClick={sendTestDigest}
              disabled={sendingTest}
              data-testid="settings-daily-summary-test"
            >
              <Send className="w-3.5 h-3.5 mr-1.5" />
              {sendingTest ? 'Sending…' : 'Send Test Now'}
            </Button>
            <Button className="rounded-full hover:bg-heda-cream"
              type="button"
              size="sm"
              variant="ghost"
              onClick={previewDigest}
              disabled={previewing}
              data-testid="settings-daily-summary-preview"
            >
              <Eye className="w-3.5 h-3.5 mr-1.5" />
              {previewing ? 'Loading…' : 'Preview Message'}
            </Button>
            {/* Diagnostic only — hits DoubleTick's free-form /text endpoint
                so the admin can tell apart API-key issues from
                template-approval issues when the daily digest is silent. */}
            <Button className="rounded-full hover:bg-heda-cream"
              type="button"
              size="sm"
              variant="ghost"
              onClick={sendTestText}
              disabled={sendingTextTest}
              title="Diagnostic: sends a plain text (no template). Helps tell if 403 is an API key issue vs a template issue."
              data-testid="settings-daily-summary-test-text"
            >
              {sendingTextTest ? 'Probing…' : 'Diagnose (text)'}
            </Button>
            {/* Verify API Key — probes DoubleTick /healthcheck +
                /messageTemplates and classifies the failure so the admin
                gets an actionable next step for recurring 403s. */}
            <Button className="rounded-full hover:bg-heda-cream"
              type="button"
              size="sm"
              variant="ghost"
              onClick={verifyDoubleTick}
              disabled={verifying}
              title="Ping DoubleTick with an API-only probe. Tells you whether the key itself is rejected vs an account/template problem."
              data-testid="settings-verify-doubletick-key"
            >
              {verifying ? 'Verifying…' : 'Verify API Key'}
            </Button>
          </div>

          {/* Iter-124 — sample reminder senders so admin can verify walk-in
              + lead reminder delivery visually, without waiting for the
              next real 10-min scan to fire. */}
          <div className="mt-2 flex flex-wrap items-center gap-2 rounded-xl border border-dashed border-heda-line bg-heda-cream px-2 py-2">
            <span className="text-[10px] uppercase tracking-wider text-heda-mute font-semibold">Test reminders:</span>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={sendTestWalkinReminder}
              disabled={testingWalkin}
              className="rounded-full border-heda-line bg-white text-heda-ink shadow-none hover:bg-white hover:border-heda-gold hover:text-heda-gold h-7 text-xs"
              data-testid="settings-test-walkin-reminder"
            >
              {testingWalkin ? 'Sending…' : 'Send sample Walk-in reminder'}
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={sendTestLeadReminder}
              disabled={testingLead}
              className="rounded-full border-heda-line bg-white text-heda-ink shadow-none hover:bg-white hover:border-heda-gold hover:text-heda-gold h-7 text-xs"
              data-testid="settings-test-lead-reminder"
            >
              {testingLead ? 'Sending…' : 'Send sample Lead follow-up'}
            </Button>
            <p className="text-[11px] text-heda-mute w-full sm:w-auto sm:flex-1">
              Sends to your first configured recipient phone. Confirms staff receive real reminders end-to-end.
            </p>
          </div>

          {/* Verify-key result banner — shows the actionable diagnostic. */}
          {verifyResult && (
            <div
              className={`mt-3 p-3 rounded-xl border ${
                verifyResult.ok
                  ? 'border-emerald-200 bg-emerald-50 text-heda-ink'
                  : 'border-red-200 bg-red-50 text-red-700'
              }`}
              data-testid="doubletick-verify-banner"
            >
              <div className="flex items-start gap-2">
                <span className="text-lg leading-none">{verifyResult.ok ? '✓' : '✗'}</span>
                <div className="flex-1 text-[12px]">
                  <p className="font-semibold mb-0.5">
                    {verifyResult.ok ? 'DoubleTick API key is valid' : 'DoubleTick API key verification failed'}
                    {verifyResult.verdict && <span className="ml-1 text-[10px] opacity-75 font-normal">({verifyResult.verdict})</span>}
                  </p>
                  <p className="mb-1">{verifyResult.message}</p>
                  {verifyResult.action && (
                    <p className="text-[11px] mt-1">
                      <span className="font-semibold uppercase tracking-wide">Next step:</span> {verifyResult.action}
                    </p>
                  )}
                  {verifyResult.probes?.length > 0 && (
                    <details className="mt-2 text-[11px]">
                      <summary className="cursor-pointer opacity-75">Raw probe responses ({verifyResult.probes.length})</summary>
                      <div className="mt-1 space-y-1 font-mono">
                        {verifyResult.probes.map((p, i) => (
                          <div key={i}>
                            <span className="opacity-70">{p.endpoint}</span> → <span className={p.status >= 200 && p.status < 300 ? 'text-emerald-700' : 'text-red-700'}>HTTP {p.status}</span> {p.body ? <>· <code className="bg-white/70 px-1 rounded-lg">{p.body}</code></> : null}
                          </div>
                        ))}
                      </div>
                    </details>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Update API Key panel — permanent fix for the recurring 403
              "works one day after redeploy" outage. Admin can paste a fresh
              key from DoubleTick's dashboard and it takes effect immediately. */}
          <div className="mt-3 border-t border-heda-line pt-3" data-testid="doubletick-key-update-panel">
            <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-heda-mute">
                  DoubleTick API Key
                </p>
                <p className="text-[11px] text-heda-mute">
                  {dtCreds ? (
                    <>
                      Currently in use: <code className="bg-white px-1 rounded-lg font-mono">
                        …{dtCreds.key_last8 || '—'}
                      </code>
                      <span className={`ml-2 text-[10px] font-semibold uppercase tracking-wider ${dtCreds.source === 'db' ? 'text-emerald-700' : 'text-amber-700'}`}>
                        {dtCreds.source === 'db' ? '• DB override active' : '• from .env'}
                      </span>
                      {dtCreds.updated_at && (
                        <span className="ml-2 text-[11px] text-heda-mute">
                          updated {new Date(dtCreds.updated_at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}
                        </span>
                      )}
                    </>
                  ) : 'Loading current key…'}
                </p>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-heda-mute">New API Key <span className="text-heda-mute">(paste from DoubleTick dashboard)</span></Label>
                <Input
                  type="password"
                  value={newApiKey}
                  onChange={(e) => setNewApiKey(e.target.value)}
                  placeholder="key_xxxxxxxxxxxxxxxxxx"
                  className="rounded-xl text-base md:text-xs font-mono h-9 bg-heda-cream/50"
                  data-testid="doubletick-new-api-key-input"
                  autoComplete="off"
                />
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-heda-mute">WABA Number <span className="text-heda-mute">(optional — digits only)</span></Label>
                <Input
                  type="text"
                  value={newWaba}
                  onChange={(e) => setNewWaba(e.target.value)}
                  placeholder={dtCreds?.waba_number || 'e.g. 919663057711'}
                  className="rounded-xl text-base md:text-xs font-mono h-9 bg-heda-cream/50"
                  data-testid="doubletick-new-waba-input"
                  autoComplete="off"
                />
              </div>
            </div>
            <div className="flex items-center gap-2 mt-2">
              <Button
                type="button"
                size="sm"
                onClick={saveDtCreds}
                disabled={savingCreds || (!newApiKey.trim() && !newWaba.trim())}
                className="rounded-xl bg-heda-ink text-white shadow-none hover:bg-black text-xs h-9"
                data-testid="doubletick-save-creds-btn"
              >
                {savingCreds ? 'Saving…' : 'Save & apply immediately'}
              </Button>
              <p className="text-[11px] text-heda-mute">
                No redeploy or backend restart — the next WhatsApp send picks it up.
              </p>
            </div>
          </div>

          {previewText && (
            <pre
              className="text-[11px] bg-heda-cream border border-heda-line rounded-lg p-3 whitespace-pre-wrap tabular-nums text-heda-ink max-h-64 overflow-auto"
              data-testid="settings-daily-summary-preview-text"
            >
              {previewText}
            </pre>
          )}

          {/* ─── Health diagnostic — surfaces production failures so the
              admin doesn't need log access to debug the 9 PM digest. ─── */}
          <div className="mt-3 border-t border-heda-line pt-3" data-testid="settings-daily-summary-health">
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs font-semibold uppercase tracking-wider text-heda-mute">
                Digest Health
              </div>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={loadDigestHealth}
                disabled={loadingHealth}
                className="rounded-full hover:bg-heda-cream h-6 text-[11px] px-2"
                data-testid="settings-daily-summary-health-refresh"
              >
                {loadingHealth ? 'Refreshing…' : 'Refresh'}
              </Button>
            </div>
            {!digestHealth ? (
              <p className="text-[11px] text-heda-mute">Loading health…</p>
            ) : (
              <>
                {((digestHealth.configured_phones || []).length === 0) && (
                  <div
                    className="mb-2 p-2 rounded-lg border border-red-200 bg-red-50 text-[11px] text-red-700"
                    data-testid="settings-daily-summary-no-phones-warning"
                  >
                    <strong>⚠ Action required:</strong> No recipient phone number is configured, so the
                    9 PM digest is being <strong>silently skipped every night</strong>. Add at least one
                    number in the field above (digits only, with country code — e.g. <code className="bg-white px-1 rounded-lg">918904002711</code>),
                    then click <em>Save Settings</em>.
                  </div>
                )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-[11px]">
                <HealthRow
                  label="Scheduler"
                  ok={digestHealth.scheduler_active && digestHealth.enabled}
                  value={
                    digestHealth.scheduler_active
                      ? digestHealth.enabled ? 'active' : 'disabled (ENABLE_DAILY_SUMMARY=0)'
                      : 'NOT RUNNING'
                  }
                />
                <HealthRow
                  label="Next run (9 PM digest)"
                  ok={Boolean(digestHealth.next_run_at)}
                  value={digestHealth.next_run_at
                    ? new Date(digestHealth.next_run_at).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })
                    : 'unscheduled'}
                />
                <HealthRow
                  label="Walk-in reminders next scan"
                  ok={Boolean(digestHealth.walkin_reminder_next_run_at)}
                  value={digestHealth.walkin_reminder_next_run_at
                    ? new Date(digestHealth.walkin_reminder_next_run_at).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })
                    : 'unscheduled — reminders WILL NOT fire'}
                  data-testid="walkin-reminder-next-run"
                />
                <HealthRow
                  label="Lead reminders next scan"
                  ok={Boolean(digestHealth.lead_reminder_next_run_at)}
                  value={digestHealth.lead_reminder_next_run_at
                    ? new Date(digestHealth.lead_reminder_next_run_at).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })
                    : 'unscheduled — reminders WILL NOT fire'}
                  data-testid="lead-reminder-next-run"
                />
                <HealthRow
                  label="Recipients configured"
                  ok={(digestHealth.configured_phones || []).length > 0}
                  value={(digestHealth.configured_phones || []).join(', ') || 'NONE — digest will be skipped'}
                />
                <HealthRow
                  label="WhatsApp template"
                  ok={Boolean(digestHealth.template_name)}
                  value={`${digestHealth.template_name} [${digestHealth.template_lang}]`}
                />
                <div className="sm:col-span-2 mt-1 p-2 rounded-lg border border-heda-line bg-heda-cream/50">
                  <div className="text-[10.5px] font-semibold uppercase tracking-wider text-heda-mute mb-1">Last attempt</div>
                  {!digestHealth.last_result ? (
                    <div className="text-heda-mute">No attempts recorded yet. Try “Send Test Now”.</div>
                  ) : (
                    <div className="space-y-1">
                      <div className="flex flex-wrap gap-x-3 gap-y-0.5">
                        <span className={digestHealth.last_result.sent ? 'text-emerald-700 font-semibold' : 'text-red-700 font-semibold'}>
                          {digestHealth.last_result.sent ? '✓ sent' : '✗ failed'}
                        </span>
                        <span className="text-heda-sub">
                          {new Date(digestHealth.last_result.at).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })}
                        </span>
                        <span className="text-heda-mute">[{digestHealth.last_result.triggered_by}]</span>
                      </div>
                      {digestHealth.last_result.reason && (
                        <div className="text-red-700">Reason: {digestHealth.last_result.reason}</div>
                      )}
                      {digestHealth.last_result.first_error && (
                        <div className="text-red-700">DoubleTick error: <code className="bg-white px-1 rounded-lg">{digestHealth.last_result.first_error}</code></div>
                      )}
                      {/* Auto-guidance banner when the last error smells
                          like a rejected API key. Recurring 403 is user's
                          #1 friction — show the fix without them having
                          to click Verify. */}
                      {digestHealth.last_result.first_error && /(403|forbidden)/i.test(digestHealth.last_result.first_error) && (
                        <div className="mt-2 p-2 rounded-lg border border-heda-gold/50 bg-amber-50 text-[11px] text-amber-700" data-testid="doubletick-403-guidance">
                          <p className="font-semibold mb-1">⚠ DoubleTick rejected the request (403 FORBIDDEN).</p>
                          <p className="mb-1">This is almost always an API-key or account issue on DoubleTick&apos;s side, not a bug in this app. Click <em>Verify API Key</em> above for a specific diagnosis, or:</p>
                          <ol className="list-decimal ml-4 space-y-0.5">
                            <li>Open <a className="underline" href="https://app.doubletick.io" target="_blank" rel="noreferrer">app.doubletick.io</a> → Settings → API Keys and confirm the key hasn&apos;t been rotated.</li>
                            <li>Confirm your DoubleTick subscription is active and your WABA number is connected.</li>
                            <li>If the key changed, share the new one with engineering to update the backend .env.</li>
                          </ol>
                        </div>
                      )}
                      {(digestHealth.last_result.recipients || []).length > 0 && (
                        <div className="text-heda-sub">
                          Recipients:{' '}
                          {digestHealth.last_result.recipients.map((r, i) => (
                            <span key={i} className="mr-2">
                              {r.to}{' '}
                              <span className={r.sent ? 'text-emerald-700' : 'text-red-700'}>
                                ({r.sent ? r.via || 'sent' : 'fail'})
                              </span>
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
              </>
            )}
          </div>
        </CardContent>
      </Card>

      <Card className="rounded-2xl border-heda-line bg-white shadow-none">
        <CardHeader className="border-b border-heda-line px-5 py-3">
          <CardTitle className="flex items-center gap-2 font-display text-[15px] font-bold text-heda-ink">
            <ShieldAlert className="w-4 h-4 text-heda-gold" /> Data Maintenance
          </CardTitle>
        </CardHeader>
        <CardContent className="px-5 py-4 space-y-2 text-xs">
          <p className="text-heda-sub">
            Scan saved quotations for the per-sqft amount-collapse bug (iter-104). Runs in dry-run mode first; you can review and then apply the heal.
          </p>
          <Button
            type="button"
            variant="outline"
            className="rounded-full border-heda-line bg-white text-heda-ink shadow-none hover:bg-white hover:border-heda-gold hover:text-heda-gold"
            onClick={() => setAuditOpen(true)}
            data-testid="settings-audit-open-btn"
          >
            <ShieldAlert className="w-3.5 h-3.5 mr-1.5" /> Audit &amp; Heal Quotations
          </Button>
          <Button
            type="button"
            variant="outline"
            className="rounded-full border-heda-line bg-white text-heda-ink shadow-none hover:bg-white hover:border-heda-gold hover:text-heda-gold ml-2"
            onClick={() => setMislabelOpen(true)}
            data-testid="settings-mislabel-open-btn"
          >
            <Tags className="w-3.5 h-3.5 mr-1.5" /> Find Mislabeled Products
          </Button>
        </CardContent>
      </Card>
      <AuditHealQuotationsDialog open={auditOpen} onOpenChange={setAuditOpen} />
      <MislabeledProductsDialog open={mislabelOpen} onOpenChange={setMislabelOpen} />

      {/* Custom Size Presets — non-standard sheet sizes (e.g. 8.5x4) staff
          adds as one-click buttons on the Plywood/Blockboard/MDF cards so
          they don't have to re-open the Custom Size dialog every time.
          Sqft is auto-calculated from the string. */}
      <Card className="rounded-2xl border-heda-line bg-white shadow-none">
        <CardHeader className="border-b border-heda-line px-5 py-3">
          <CardTitle className="flex items-center gap-2 font-display text-[15px] font-bold text-heda-ink">
            <Ruler className="w-4 h-4 text-heda-gold" /> Custom Size Presets
          </CardTitle>
        </CardHeader>
        <CardContent className="px-5 py-4 space-y-2">
          <p className="text-[11px] text-heda-sub">
            Add non-standard sheet sizes (in <strong>feet</strong>) that staff frequently quote — e.g.{' '}
            <code className="bg-heda-cream px-1 rounded-lg">8.5x4</code>,{' '}
            <code className="bg-heda-cream px-1 rounded-lg">9x3.5</code>. Each preset shows as a one-click size
            button on Plywood, Blockboard, MDF, WPC/PVC &amp; Doors cards. Standard sizes (8x4, 10x4 etc.) are
            already built-in — no need to re-add them here.
          </p>
          <div className="space-y-1.5" data-testid="settings-size-presets-list">
            {(data.custom_size_presets || []).map((preset, i) => (
              <div key={i} className="flex gap-2 items-center">
                <Input
                  value={preset}
                  onChange={e => {
                    const next = [...(data.custom_size_presets || [])];
                    next[i] = e.target.value;
                    setData({ ...data, custom_size_presets: next });
                  }}
                  placeholder="8.5x4"
                  className="rounded-xl max-w-[160px] tabular-nums text-base md:text-sm bg-heda-cream/50"
                  data-testid={`settings-size-preset-input-${i}`}
                />
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={() => setData({
                    ...data,
                    custom_size_presets: (data.custom_size_presets || []).filter((_, j) => j !== i),
                  })}
                  data-testid={`settings-size-preset-remove-${i}`}
                  className="rounded-full hover:bg-heda-cream text-red-700 hover:text-red-700"
                >
                  <X className="w-3.5 h-3.5" />
                </Button>
              </div>
            ))}
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => setData({
                ...data,
                custom_size_presets: [...(data.custom_size_presets || []), ''],
              })}
              data-testid="settings-size-preset-add"
              className="rounded-full border-heda-line bg-white text-heda-ink shadow-none hover:bg-white hover:border-heda-gold hover:text-heda-gold mt-1"
            >
              + Add Preset
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card className="rounded-2xl border-heda-line bg-white shadow-none">
        <CardHeader className="border-b border-heda-line px-5 py-3">
          <CardTitle className="flex items-center gap-2 font-display text-[15px] font-bold text-heda-ink">
            <Download className="w-4 h-4 text-heda-gold" /> Data Backup
          </CardTitle>
        </CardHeader>
        <CardContent className="px-5 py-4 space-y-3">
          <p className="text-[11px] text-heda-sub leading-relaxed">
            Downloads a full copy of all data as a zip (one JSON file per collection plus a
            manifest with document counts). <strong>Recommended weekly.</strong> Password hashes
            and any stored API keys are stripped before export. Keep the file somewhere private —
            it contains customer, quotation and stock records.
          </p>
          <Button
            type="button"
            onClick={handleDownloadBackup}
            disabled={backingUp}
            variant="outline"
            className="rounded-full border-heda-line bg-white text-heda-ink shadow-none hover:bg-white hover:border-heda-gold hover:text-heda-gold"
            data-testid="settings-download-backup"
          >
            <Download className="w-4 h-4 mr-1.5" />
            {backingUp ? 'Preparing zip…' : 'Download Backup'}
          </Button>
        </CardContent>
      </Card>

      <div className="flex justify-end gap-2 sticky bottom-0 bg-heda-cream/95 backdrop-blur py-3 border-t border-heda-line">
        <Button
          onClick={save}
          disabled={saving}
          className="rounded-xl bg-heda-olive text-white shadow-none hover:bg-heda-olive-dark"
          data-testid="settings-save"
        >
          <Save className="w-4 h-4 mr-1.5" /> {saving ? 'Saving…' : 'Save Settings'}
        </Button>
      </div>
    </div>
  );
}


// Tiny status row used by the Digest Health panel — green dot when healthy,
// amber when warning, red when broken.
function HealthRow({ label, ok, value, ...rest }) {
  return (
    <div className="flex items-start gap-2" {...rest}>
      <span
        className={`mt-1 inline-block w-2 h-2 rounded-full flex-shrink-0 ${
          ok ? 'bg-emerald-700' : 'bg-red-700'
        }`}
      />
      <div className="flex-1 min-w-0">
        <div className="text-[10.5px] font-semibold uppercase tracking-wider text-heda-mute">{label}</div>
        <div className="text-[12px] text-heda-ink break-words">{value}</div>
      </div>
    </div>
  );
}
